import * as React from 'react';
import Box from '@mui/material/Box';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridColumnMenuProps,
  GridColumnMenu,
  GridLoadingOverlayProps,
  LoadingOverlayPropsOverrides,
  GridRenderCellParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { CircularProgress, LinearProgress } from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  searchItems,
  setSearchInput,
  setSearchLoading,
  setSelectedItems,
} from '../../../../state/itemSearchModalSlice';
import { useDispatch, useSelector } from '../../../../state';
import type { ItemSearchInput } from '@repo/api-contract';
import { useCallback } from 'react';
import { debounce } from '@mui/material/utils';
import SearchByDropdown from './SearchByDropdown';
import { useTranslation } from 'react-i18next';
import {
  GRID_CHECKBOX_COLUMN_ORDER,
  EMPTY_LEADING_GRID_COLUMNS,
  usePersistedDataGridColumnPreferences,
} from '../../../../util/hooks';
import type { DataGridTableId } from '../../../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../../dataGrid/DataGridTranslatedColumnHeader';
import hexToRGBA from '../../../../util/converters/hexToRGBA';

const TABLE_ID = 'search-items' satisfies DataGridTableId;
const KEY_ITEMS_SEARCH = 'team.Modals.ItemsSearch';
const KEY_GLOBAL_COMMON = 'team.Global.Common';

interface LoaderProps
  extends Omit<GridLoadingOverlayProps, 'color'>,
    LoadingOverlayPropsOverrides {
  color?: string;
}

const Loader = (props: LoaderProps) => {
  return <LinearProgress color={'primary'} />;
};

function CustomToolbar() {
  const dispatch = useDispatch();
  const { t: tMIS } = useTranslation([], {
    keyPrefix: 'team.Modals.ItemsSearch',
  });
  const { searchInput, searchBy, searchLoading } = useSelector(
    (store) => store.itemSearchModalSlice,
  );
  const { team } = useSelector((store) => store.userSlice);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: ItemSearchInput = {
        query: searchQuery,
        searchBy,
        size: 100,
        page: 0,
      };
      dispatch(searchItems(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <SearchByDropdown />
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setSearchLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label={tMIS('Search for items...')}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <React.Fragment>
              {searchLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
            </React.Fragment>
          ),
        }}
      />
    </GridToolbarContainer>
  );
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuColumnsItem: null,
        columnMenuFilterItem: null,
      }}
    />
  );
}

export default function Table({ multiSelect }: { multiSelect: boolean }) {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { resp, searchLoading } = useSelector(
    (store) => store.itemSearchModalSlice,
  );

  const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
    let finalSelectionModel = newSelectionModel;
    if (!multiSelect && newSelectionModel.length > 1) {
      const lastId = newSelectionModel[newSelectionModel.length - 1];
      if (lastId !== undefined) {
        finalSelectionModel = [lastId];
      }
    }

    const rows = resp ? resp.results : [];
    const selected = rows.filter((row) =>
      finalSelectionModel.includes(row.ItemId),
    );
    dispatch(setSelectedItems(selected));
  };

  const baseColumns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'vendorItemNumber',
        headerName: 'Item Code',
        type: 'string',
        flex: 1,
        valueGetter: (value, row) => {
          return row.details.vendorItemNumber;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Item Code"
            keyPrefix={KEY_ITEMS_SEARCH}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        valueGetter: (value, row) => {
          return row.details.description;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Description"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'unit',
        headerName: 'Unit',
        flex: 1,
        valueGetter: (value, row) => {
          return row.details.unit;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Unit"
            keyPrefix={KEY_ITEMS_SEARCH}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'customerPrice',
        headerName: 'Customer Price',
        type: 'string',
        flex: 1,
        valueGetter: (value, row) => {
          return row.details.customerPrice;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Price"
            keyPrefix={KEY_ITEMS_SEARCH}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
    ],
    [],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: multiSelect
      ? GRID_CHECKBOX_COLUMN_ORDER
      : EMPTY_LEADING_GRID_COLUMNS,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <DataGridPro
      apiRef={apiRef}
      columnHeaderHeight={56}
      sx={{
        border: 'none',
        padding: '1rem',
        '.MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
          backgroundColor: '#ECECEC',
        },
        '.MuiDataGrid-row.Mui-selected': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-scrollbar--horizontal': { display: 'none' },
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
          whiteSpace: 'normal',
          lineHeight: 1.2,
        },
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
      }}
      rows={
        resp
          ? resp.results.map((item) => ({
              ...item,
              id: item.ItemId,
            }))
          : []
      }
      onRowSelectionModelChange={handleSelectionChange}
      checkboxSelection={multiSelect}
      disableRowSelectionOnClick={multiSelect}
      loading={searchLoading}
      columns={columns}
      columnVisibilityModel={persistedPrefs.columnVisibilityModel}
      onColumnVisibilityModelChange={
        persistedPrefs.onColumnVisibilityModelChange
      }
      onColumnWidthChange={persistedPrefs.onColumnWidthChange}
      onColumnOrderChange={persistedPrefs.onColumnOrderChange}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 100,
          },
        },
      }}
      pageSizeOptions={[1]}
      pinnedColumns={
        multiSelect ? { left: [GRID_CHECKBOX_SELECTION_FIELD] } : undefined
      }
      disableColumnMenu
      autoHeight={false}
      slots={{
        loadingOverlay: Loader,
        toolbar: CustomToolbar,
        columnMenu: CustomColumnMenu,
      }}
    />
  );
}
